import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import OrderCard from './components/OrderCard';
import InvoiceOrderModal from './components/InvoiceOrderModal';
import ConfirmPaymentCloseModal, {
  invoiceCloseActionLabel,
} from './components/ConfirmPaymentCloseModal';
import AssignOrderModal from './components/AssignOrderModal';
import CreateRecurringOrderModal from './components/CreateRecurringOrderModal';
import ReorderOrderModal, { type ReorderOrderConfirmPayload } from './components/ReorderOrderModal';
import OrderFilterDropdown, {
  DEFAULT_ORDER_FILTERS,
  type OrderListFilters,
} from './components/OrderFilterDropdown';
import type { Order, OrderServicerInfo } from '@/types/order';
import {
  ApiRequestError,
  canViewOrderTeamPrivacy,
  fetchSystemUserInfo,
  isUserInTeam,
  isTeamAdmin,
  needsTeamPrompt,
  showApiResultModal,
  type SystemUserInfo,
} from '@/api';
import NoTeamPrompt from '@/components/team/NoTeamPrompt';
import {
  canTeamAdminEditOrderBeforeComplete,
  canTeamAdminEditWorkHours,
  fetchOrderListPage,
  orderHasAssignedStaff,
  isAssignedServiceFullyCompleted,
  OrderCloseAfterPaymentError,
  OrderClosedBeforePaymentError,
  patchOrderAfterAssignment,
  patchOrderAfterOperation,
  patchOrderAfterServiceCompleted,
  patchOrderAfterStatusChange,
  postConfirmInvoicePaymentAndClose,
  postMarkServiceCompleted,
  postAdminReorder,
  postAssignOrder,
  postOrderBillingAdjustment,
  resolveInvoiceCloseAction,
  toFilterEndDate,
  toFilterStartDate,
  uiTabToListStatus,
  type FetchOrderListPageParams,
  type OrderListStatistics,
} from '@/api/modules/order';
import { resetPageScroll } from '@/lib/resetPageScroll';
import { rememberInvoiceSent } from '@/lib/invoiceSentOrders';
import type { OrderBillingValues } from '@/lib/orderBilling';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

const PAGE_SIZE = 10;

function filtersAreActive(filters: OrderListFilters): boolean {
  return (
    filters.startDate !== ''
    || filters.endDate !== ''
    || filters.orderState !== ''
  );
}

/**
 * Build order list request from Filter selections.
 * - start_date / end_date
 * - status (order state)
 */
function buildOrderListParams(
  page: number,
  filters: OrderListFilters,
  asTeamAdmin: boolean,
): FetchOrderListPageParams {
  const startDate = filters.startDate.trim();
  const endDate = (filters.endDate || filters.startDate).trim();
  const status = uiTabToListStatus(filters.orderState || 'all');

  return {
    page,
    pageSize: PAGE_SIZE,
    asTeamAdmin,
    ...(status ? { status } : {}),
    ...(startDate ? { start_date: toFilterStartDate(startDate) } : {}),
    ...(endDate ? { end_date: toFilterEndDate(endDate) } : {}),
  };
}

function buildFetchKey(
  filters: OrderListFilters,
  page: number,
  asTeamAdmin: boolean,
): string {
  return [
    filters.orderState,
    filters.startDate,
    filters.endDate,
    asTeamAdmin ? 'admin' : 'op',
    page,
  ].join('|');
}

let pendingFirstPageFetch: {
  key: string;
  promise: ReturnType<typeof fetchOrderListPage>;
} | null = null;

function fetchFirstPageDeduped(
  key: string,
  params: Parameters<typeof fetchOrderListPage>[0],
) {
  if (pendingFirstPageFetch?.key === key) {
    return pendingFirstPageFetch.promise;
  }
  const promise = fetchOrderListPage(params).finally(() => {
    if (pendingFirstPageFetch?.key === key) {
      pendingFirstPageFetch = null;
    }
  });
  pendingFirstPageFetch = { key, promise };
  return promise;
}

export default function HomePage({ variant = 'ticket' }: { variant?: 'ticket' | 'team' }) {
  const navigate = useNavigate();
  const listReturnPath = variant === 'team' ? '/team/orders' : '/orders';
  const [search, setSearch] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [profileReady, setProfileReady] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [completingOrderId, setCompletingOrderId] = useState<string | null>(null);
  const [invoiceOrder, setInvoiceOrder] = useState<Order | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<Order | null>(null);
  const [recurringOrder, setRecurringOrder] = useState<Order | null>(null);
  const [reorderOrder, setReorderOrder] = useState<Order | null>(null);
  const [reorderSubmitting, setReorderSubmitting] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [operationOrder, setOperationOrder] = useState<Order | null>(null);
  const [operationMode, setOperationMode] = useState<'assign' | 'billing'>('billing');
  const [operationSubmitting, setOperationSubmitting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState<OrderListFilters>(DEFAULT_ORDER_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<OrderListFilters>(DEFAULT_ORDER_FILTERS);

  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [listStatistics, setListStatistics] = useState<OrderListStatistics | null>(null);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const listRequestSeqRef = useRef(0);
  const infiniteScrollEnabledRef = useRef(false);

  const inTeam = isUserInTeam(profile);
  const canViewCustomer =
    isTeamAdmin(profile) || profile?.is_admin === true || Number(profile?.team_role) === 1;
  // Ticket 列表只走成员接口。团队全部订单才走 /system/team/order/list。
  const listAsTeamAdmin = variant === 'team';

  useEffect(() => {
    let cancelled = false;
    fetchSystemUserInfo()
      .then((info) => {
        if (cancelled) return;
        setProfile(info);
        setCurrentUserId(String(info.userID));
        setProfileError('');
      })
      .catch((e) => {
        if (cancelled) return;
        setProfile(null);
        setProfileError(e instanceof ApiRequestError ? e.message : 'Failed to load profile.');
      })
      .finally(() => {
        if (!cancelled) setProfileReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    if (!profileReady || !inTeam) {
      setListLoading(false);
      setOrders([]);
      setTotal(0);
      setListStatistics(null);
      setListError(null);
      return;
    }

    const seq = ++listRequestSeqRef.current;
    let cancelled = false;
    infiniteScrollEnabledRef.current = false;

    setListLoading(true);
    setListError(null);
    setPage(1);
    pageRef.current = 1;
    loadingMoreRef.current = false;

    void (async () => {
      try {
        const fetchKey = buildFetchKey(appliedFilters, 1, listAsTeamAdmin);
        const { orders: rows, total: t, statistics } = await fetchFirstPageDeduped(
          fetchKey,
          buildOrderListParams(1, appliedFilters, listAsTeamAdmin),
        );
        if (cancelled || seq !== listRequestSeqRef.current) return;
        setOrders(rows);
        setTotal(t);
        setListStatistics(statistics ?? null);
      } catch (e) {
        if (cancelled || seq !== listRequestSeqRef.current) return;
        setOrders([]);
        setTotal(0);
        setListStatistics(null);
        setListError(e instanceof Error ? e.message : 'Failed to load orders');
      } finally {
        if (cancelled || seq !== listRequestSeqRef.current) return;
        setListLoading(false);
        resetPageScroll();
        window.setTimeout(() => {
          if (seq === listRequestSeqRef.current) {
            infiniteScrollEnabledRef.current = true;
          }
        }, 200);
      }
    })();

    return () => {
      cancelled = true;
      infiniteScrollEnabledRef.current = false;
    };
  }, [appliedFilters, profileReady, inTeam, listAsTeamAdmin]);

  const applyFilters = useCallback(() => {
    const startDate = draftFilters.startDate;
    const endDate = draftFilters.endDate || draftFilters.startDate;
    setAppliedFilters({
      startDate,
      endDate,
      orderState: draftFilters.orderState,
    });
    setShowFilters(false);
  }, [draftFilters]);

  const resetFilters = useCallback(() => {
    setDraftFilters(DEFAULT_ORDER_FILTERS);
    setAppliedFilters(DEFAULT_ORDER_FILTERS);
    setShowFilters(false);
  }, []);

  const closeFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const loadMore = useCallback(async () => {
    if (!inTeam) return;
    if (!infiniteScrollEnabledRef.current) return;
    if (loadingMoreRef.current || listLoading || loadingMore) return;
    if (total === 0) return;
    if (orders.length >= total) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    try {
      const { orders: rows, total: t, statistics } = await fetchOrderListPage(
        buildOrderListParams(nextPage, appliedFilters, listAsTeamAdmin),
      );
      if (rows.length === 0) {
        setTotal((prev) => Math.min(prev, orders.length));
        return;
      }
      setTotal(t);
      if (statistics) setListStatistics(statistics);
      setPage(nextPage);
      pageRef.current = nextPage;
      setOrders((prev) => {
        const seen = new Set(prev.map((o) => o.id));
        const next = [...prev];
        for (const r of rows) {
          if (!seen.has(r.id)) {
            seen.add(r.id);
            next.push(r);
          }
        }
        return next;
      });
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [inTeam, listLoading, loadingMore, orders.length, total, appliedFilters, listAsTeamAdmin]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || listLoading || orders.length === 0 || orders.length >= total) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!infiniteScrollEnabledRef.current || !entries[0]?.isIntersecting) return;
        void loadMore();
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore, listLoading, orders.length, total]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const q = search.trim().toLowerCase();
      return (
        !q
        || o.id.toLowerCase().includes(q)
        || o.customer.name.toLowerCase().includes(q)
        || o.serviceType.toLowerCase().includes(q)
      );
    });
  }, [search, orders]);

  const openOrderDetail = useCallback((order: Order) => {
    navigate(`/orders/${encodeURIComponent(order.id)}`, { state: { from: listReturnPath } });
  }, [listReturnPath, navigate]);

  const handleConfirmReorder = useCallback(async (payload: ReorderOrderConfirmPayload) => {
    if (!reorderOrder || reorderSubmitting) return;
    setReorderSubmitting(true);
    try {
      const result = await postAdminReorder({
        orderId: reorderOrder.id,
        date: payload.date,
        time: payload.time,
        otherInfo: payload.otherInfo,
      });
      setReorderOrder(null);
      showApiResultModal({ message: result.message, success: true });
      navigate('/team', { state: { focusDate: payload.date } });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to reorder.',
        success: false,
      });
    } finally {
      setReorderSubmitting(false);
    }
  }, [navigate, reorderOrder, reorderSubmitting]);

  const patchListedOrder = useCallback((orderId: string, patch: (order: Order) => Order) => {
    setOrders((prev) => prev.map((order) => (order.id === orderId ? patch(order) : order)));
  }, []);

  const handleConfirmOperation = useCallback(async (
    operatorIds: string[] | undefined,
    billing: OrderBillingValues,
    staff: OrderServicerInfo[],
  ) => {
    if (!operationOrder || operationSubmitting) return;
    if (operationMode === 'assign' && (!operatorIds || operatorIds.length === 0)) return;
    setOperationSubmitting(true);
    try {
      const result = operationMode === 'assign'
        ? await postAssignOrder({
            orderId: operationOrder.id,
            operatorIds: operatorIds ?? [],
            serviceDuration: billing.serviceDuration,
          })
        : await postOrderBillingAdjustment(operationOrder, billing, operatorIds);
      patchListedOrder(operationOrder.id, (order) => (
        operationMode === 'assign'
          ? patchOrderAfterAssignment(
              { ...order, serviceDuration: billing.serviceDuration },
              operatorIds ?? [],
              undefined,
              staff,
            )
          : patchOrderAfterOperation(order, operatorIds, billing, staff)
      ));
      setOperationOrder(null);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to update order.',
        success: false,
      });
    } finally {
      setOperationSubmitting(false);
    }
  }, [operationMode, operationOrder, operationSubmitting, patchListedOrder]);

  const markInvoiceSent = useCallback((order: Order) => {
    rememberInvoiceSent(order.id);
    patchListedOrder(order.id, (current) => ({ ...current, invoiceSent: true }));
  }, [patchListedOrder]);

  const handleConfirmPaymentClose = useCallback(async (
    target: Order,
    details?: { paymentMethod?: 'transfer' | 'cash'; paymentDate?: string },
  ) => {
    if (paymentSubmitting) return;
    const action = resolveInvoiceCloseAction(target);
    if (!action) return;
    setPaymentSubmitting(true);
    try {
      const result = await postConfirmInvoicePaymentAndClose(target, action, details);
      patchListedOrder(target.id, (order) =>
        patchOrderAfterStatusChange(
          { ...order, payStatus: result.payStatus, invoiceSent: true },
          result.closed ? 'completed' : order.status,
          result.closed ? 3 : (order.rawStatus ?? 2),
        ),
      );
      setPaymentOrder(null);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      if (e instanceof OrderClosedBeforePaymentError) {
        patchListedOrder(target.id, (order) =>
          patchOrderAfterStatusChange(
            { ...order, payStatus: 0, invoiceSent: true },
            'completed',
            3,
          ),
        );
        setPaymentOrder(null);
      } else if (e instanceof OrderCloseAfterPaymentError) {
        patchListedOrder(target.id, (order) => ({ ...order, payStatus: 1, invoiceSent: true }));
        setPaymentOrder((current) => (current && current.id === target.id ? { ...current, payStatus: 1, invoiceSent: true } : current));
      }
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to confirm payment.',
        success: false,
      });
    } finally {
      setPaymentSubmitting(false);
    }
  }, [paymentSubmitting, patchListedOrder]);

  const handleCompleteService = useCallback(async (target: Order) => {
    if (completingOrderId) return;

    setCompletingOrderId(target.id);
    try {
      const result = await postMarkServiceCompleted({ orderId: target.id });
      const allCompleted = isAssignedServiceFullyCompleted(target, result.allCompleted);

      setOrders((prev) =>
        prev.map((order) => (
          order.id === target.id
            ? patchOrderAfterServiceCompleted(order, allCompleted)
            : order
        )),
      );

      if (allCompleted) {
        setListStatistics((prev) =>
          prev
            ? {
                ...prev,
                inProgress: Math.max(0, prev.inProgress - 1),
                serviceDone: prev.serviceDone + 1,
              }
            : prev,
        );
      }

      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to mark service completed.',
        success: false,
      });
    } finally {
      setCompletingOrderId(null);
    }
  }, [completingOrderId]);

  return (
    <div className={variant === 'team' ? 'min-h-screen bg-[#f8f9fa] pb-6' : 'ui-tab-page bg-[#f8f9fa]'}>
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
          showFilters && inTeam ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden
        onClick={closeFilters}
      />

      {variant === 'team' ? (
        <SubpageHeader
          title="Team All Orders"
          subtitle="Every order in your team"
          onBack={() => navigate('/team')}
          className="z-50"
        >
          {inTeam ? (
            <div className="px-4 pb-3">
              <OrderListToolbar
                search={search}
                onSearchChange={setSearch}
                showFilters={showFilters}
                filtersActive={filtersAreActive(appliedFilters)}
                onToggleFilters={() => {
                  setShowFilters((prev) => {
                    const next = !prev;
                    if (next) {
                      setDraftFilters({
                        ...appliedFilters,
                        orderState: appliedFilters.orderState,
                      });
                    }
                    return next;
                  });
                }}
                draftFilters={draftFilters}
                onDraftChange={setDraftFilters}
                onApply={applyFilters}
                onReset={resetFilters}
              />
            </div>
          ) : null}
        </SubpageHeader>
      ) : (
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 pt-6 pb-3">
        {inTeam && (
          <OrderListToolbar
            search={search}
            onSearchChange={setSearch}
            showFilters={showFilters}
            filtersActive={filtersAreActive(appliedFilters)}
            onToggleFilters={() => {
              setShowFilters((prev) => {
                const next = !prev;
                if (next) {
                  setDraftFilters({
                    ...appliedFilters,
                    orderState: appliedFilters.orderState,
                  });
                }
                return next;
              });
            }}
            draftFilters={draftFilters}
            onDraftChange={setDraftFilters}
            onApply={applyFilters}
            onReset={resetFilters}
          />
        )}
      </div>
      )}

      <div className="relative z-0 px-4 pt-4">
        {!profileReady ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <i className="ri-loader-4-line text-3xl text-green-500 animate-spin mb-3 block mx-auto" />
            <div className="text-sm text-gray-500 font-medium">Checking team…</div>
          </div>
        ) : profileError && !profile ? (
          <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {profileError}
          </div>
        ) : needsTeamPrompt(profile) ? (
          <NoTeamPrompt profile={profile} />
        ) : (
        <div>
          {listError && (
            <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {listError}
            </div>
          )}

          <div className="space-y-3">
            {listLoading && orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                <i className="ri-loader-4-line text-3xl text-green-500 animate-spin mb-3 block mx-auto" />
                <div className="text-sm text-gray-500 font-medium">Loading orders…</div>
              </div>
            ) : filteredOrders.length > 0 ? (
              <>
                {filteredOrders.map((order) => {
                  const isStaffList = variant !== 'team';
                  const isOrderTeamAdmin = !isStaffList && canViewOrderTeamPrivacy(profile, order.teamId, order.teamName);
                  const paymentAction = isOrderTeamAdmin ? resolveInvoiceCloseAction(order) : null;
                  return (
                  <OrderCard
                    key={order.id}
                    order={order}
                    currentUserId={currentUserId}
                    canViewCustomer={!isStaffList && canViewCustomer}
                    onClick={() => openOrderDetail(order)}
                    onComplete={() => void handleCompleteService(order)}
                    completeSubmitting={completingOrderId === order.id}
                    onAssign={
                      canTeamAdminEditWorkHours(order, isOrderTeamAdmin) && !orderHasAssignedStaff(order)
                        ? () => {
                            setOperationMode('assign');
                            setOperationOrder(order);
                          }
                        : undefined
                    }
                    onOrderOperation={
                      canTeamAdminEditOrderBeforeComplete(order, isOrderTeamAdmin)
                        ? () => {
                            setOperationMode('billing');
                            setOperationOrder(order);
                          }
                        : undefined
                    }
                    onApplyInvoice={
                      isOrderTeamAdmin
                        ? () => setInvoiceOrder(order)
                        : undefined
                    }
                    onConfirmPayment={
                      paymentAction
                        ? () => setPaymentOrder(order)
                        : undefined
                    }
                    confirmPaymentSubmitting={paymentSubmitting && paymentOrder?.id === order.id}
                    confirmPaymentLabel={
                      paymentAction ? invoiceCloseActionLabel(paymentAction) : undefined
                    }
                    onCreateRecurring={
                      isOrderTeamAdmin && order.status === 'completed'
                        ? () => setRecurringOrder(order)
                        : undefined
                    }
                    onReorder={
                      isOrderTeamAdmin ? () => setReorderOrder(order) : undefined
                    }
                  />
                  );
                })}
                <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
                {loadingMore && (
                  <div className="text-center py-3 text-gray-400 text-xs font-medium flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line text-base animate-spin" />
                    Loading more…
                  </div>
                )}
                {!listLoading && !loadingMore && total > 0 && orders.length >= total && (
                  <div className="text-center py-2 text-gray-300 text-[11px]">All orders loaded</div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                <i className="ri-file-search-line text-4xl text-gray-300 mb-3 block" />
                <div className="text-sm text-gray-400 font-medium">No orders found</div>
                <div className="text-xs text-gray-300 mt-1">Try adjusting your search or filter</div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      <InvoiceOrderModal
        order={invoiceOrder}
        onClose={() => setInvoiceOrder(null)}
        onInvoiceSent={markInvoiceSent}
        onConfirmPayment={(sent) => {
          markInvoiceSent(sent);
          setInvoiceOrder(null);
          setPaymentOrder({ ...sent, invoiceSent: true });
        }}
      />

      <ConfirmPaymentCloseModal
        order={paymentOrder}
        action={paymentOrder ? resolveInvoiceCloseAction(paymentOrder) : null}
        submitting={paymentSubmitting}
        onClose={() => {
          if (!paymentSubmitting) setPaymentOrder(null);
        }}
        onConfirm={(details) => {
          if (!paymentOrder) return;
          void handleConfirmPaymentClose(paymentOrder, details);
        }}
      />

      <CreateRecurringOrderModal
        order={recurringOrder}
        onClose={() => setRecurringOrder(null)}
      />

      <ReorderOrderModal
        order={reorderOrder}
        submitting={reorderSubmitting}
        onClose={() => {
          if (!reorderSubmitting) setReorderOrder(null);
        }}
        onConfirm={(payload) => void handleConfirmReorder(payload)}
      />

      <AssignOrderModal
        order={operationOrder}
        mode={operationMode}
        submitting={operationSubmitting}
        onClose={() => {
          if (!operationSubmitting) setOperationOrder(null);
        }}
        onConfirm={(ids, billing, staff) => void handleConfirmOperation(ids, billing, staff)}
      />
    </div>
  );
}

function OrderListToolbar({
  search,
  onSearchChange,
  showFilters,
  filtersActive,
  onToggleFilters,
  draftFilters,
  onDraftChange,
  onApply,
  onReset,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  showFilters: boolean;
  filtersActive: boolean;
  onToggleFilters: () => void;
  draftFilters: OrderListFilters;
  onDraftChange: (next: OrderListFilters | ((prev: OrderListFilters) => OrderListFilters)) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg px-3 h-10 border border-gray-200 focus-within:border-green-500 transition-colors">
          <i className="ri-search-line text-gray-400 text-sm flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search order ID / customer name…"
            className="flex-1 bg-transparent text-gray-700 text-sm font-normal outline-none placeholder:font-normal placeholder:text-xs placeholder:text-gray-400"
          />
          {search ? (
            <button type="button" onClick={() => onSearchChange('')} className="cursor-pointer">
              <i className="ri-close-circle-fill text-gray-300" />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onToggleFilters}
          className={`relative h-10 px-3.5 rounded-lg border text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap bg-white transition-colors ${
            showFilters || filtersActive
              ? 'border-green-500 text-green-600'
              : 'border-gray-200 text-gray-500'
          }`}
          aria-expanded={showFilters}
          aria-controls="order-filter-panel"
        >
          <i className="ri-equalizer-3-line" />
          <span className="text-xs font-medium">Filter</span>
          <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
          {filtersActive ? (
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-green-600" />
          ) : null}
        </button>
      </div>
      <div id="order-filter-panel">
        <OrderFilterDropdown
          open={showFilters}
          draft={draftFilters}
          onDraftChange={onDraftChange}
          onApply={onApply}
          onReset={onReset}
        />
      </div>
    </div>
  );
}
